import { mapState } from 'vuex';
import ApiService from '@/services/apiService';
import { get, cloneDeep, set } from 'lodash';
import codemirror from '@/mixins/codemirror';
import iconsMixin from '@/components/core/dtwin/state/bridge/iconsMixin';
import multiSearchMixin from '@/mixins/multiSearchVirtualDevices';
import { versionsHasUnsyncedChanges, getUnsyncedChanges } from '@/utils/deviceTemplateUtils';
import { createComparisonOperatorOptions, filterComparisonOperators } from '@/utils/comparisonOperators';
export default {
  name: 'DeviceTemplatesDetails',
  mixins: [iconsMixin, multiSearchMixin, codemirror],
  data() {
    return {
      accounts: [],
      account: null,
      parentTree: null,
      isDeviceMetaFormValid: true,
      isTemplateMetaFormValid: true,
      isDataStatesFormValid: true,
      isDraftMode: false,
      useExpertMode: false,
      showHealthSyncPrompt: false,
      healthIsSyncing: false,
      templateInformation: {
        templateIsChanged: false,
        isProductiveVersion: false,
        hasUnsyncedChanges: false,
        linkedDevicesTotalCount: 0,
        unsyncedChanges: []
      },
      linkedDevicesFetchError: null,
      /** Tracks which template id `linkedDevicesTotalCount` belongs to; avoids clearing the Related devices tab badge on draft ↔ productive refetch (same id). */
      linkedDevicesCountForTemplateId: null
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'image', 'color', 'permissions']),
    itemId() {
      return this.$route.params.id;
    },
    availableDurations() {
      const baseTranslation = 'DigitalTwin.DigitalTwin.visualization.timeRanges.count';
      return [15, 30].map(min => {
        return {
          value: min * 60,
          label: this.$i18n_tc(`${baseTranslation}Min`, min, {
            n: min
          })
        };
      }).concat([1, 3, 6, 12, 24].map(hour => {
        return {
          value: hour * 3600,
          label: this.$i18n_tc(`${baseTranslation}Hour`, hour, {
            n: hour
          })
        };
      })).concat([2, 7, 14].map(day => {
        return {
          value: day * 86400,
          label: this.$i18n_tc(`${baseTranslation}Day`, day, {
            n: day
          })
        };
      })).concat([1, 3, 6].map(mon => {
        return {
          value: mon * 30 * 86400,
          label: this.$i18n_tc(`${baseTranslation}Month`, mon, {
            n: mon
          })
        };
      })).concat([{
        value: 365 * 86400,
        label: this.$i18n_t('DigitalTwin.DigitalTwin.visualization.timeRanges.1Y')
      }]);
    },
    healthOperatorBaseOptions() {
      const translate = (...args) => this.$i18n_t(...args);
      return createComparisonOperatorOptions(translate, {
        includeValueOlderThan: true
      });
    },
    deviceMetaInformationTab() {
      return {
        label: this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.deviceMetaInformation'),
        urlHashbang: 'tab-device-meta',
        rows: [{
          columns: [{
            cols: 12,
            sm: 12,
            md: 12,
            lg: 12,
            slotName: 'deviceMetaInformation'
          }]
        }]
      };
    },
    templateMetaInformationTab() {
      return {
        label: this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.templateMetaInformation'),
        urlHashbang: 'tab-template-meta',
        rows: [{
          columns: [{
            cols: 12,
            sm: 12,
            md: 12,
            lg: 12,
            slotName: 'templateMetaInformation'
          }]
        }]
      };
    },
    dataStatesTab() {
      return {
        label: this.$i18n_t('DigitalTwin.DigitalTwin.dataStates'),
        urlHashbang: 'tab-data-states',
        rows: [{
          columns: [{
            cols: 12,
            sm: 12,
            md: 12,
            lg: 12,
            slotName: 'dataStates'
          }]
        }]
      };
    },
    ruleEditorTab() {
      return {
        label: this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.ruleEditor'),
        urlHashbang: 'tab-rule-editor',
        rows: [{
          columns: [{
            cols: 12,
            sm: 12,
            md: 12,
            lg: 12,
            slotName: 'ruleEditor'
          }]
        }]
      };
    },
    dataRoutingTab() {
      return {
        label: this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.dataRouting'),
        urlHashbang: 'tab-data-routing',
        rows: [{
          columns: [{
            cols: 12,
            sm: 12,
            md: 12,
            lg: 12,
            slotName: 'dataRouting'
          }]
        }]
      };
    },
    linkedDevicesTab() {
      return {
        label: this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.relatedDevices'),
        badge: this.templateInformation.linkedDevicesTotalCount,
        urlHashbang: 'linked-devices',
        eager: true,
        rows: [{
          columns: [{
            cols: 12,
            sm: 12,
            md: 12,
            lg: 12,
            slotName: 'linkedDevices'
          }]
        }]
      };
    },
    unsyncedChangesTab() {
      return {
        label: this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.unsyncedChanges'),
        badge: this.templateInformation.unsyncedChanges.length,
        badgeColor: 'orange',
        urlHashbang: 'tab-unsynced-changes',
        rows: [{
          columns: [{
            cols: 12,
            sm: 12,
            md: 12,
            lg: 12,
            slotName: 'unsyncedChanges'
          }]
        }]
      };
    },
    tabs() {
      return [this.templateMetaInformationTab, this.deviceMetaInformationTab, this.dataStatesTab, this.ruleEditorTab, this.dataRoutingTab, this.healthConfigurationTab, this.linkedDevicesTab, this.unsyncedChangesTab];
    },
    formValidCompute() {
      return this.isDeviceMetaFormValid && this.isTemplateMetaFormValid;
    },
    linkedDevicesPredefinedSearch() {
      return [{
        param: 'deviceTemplate',
        search: {
          value: this.itemId
        }
      }];
    },
    headerButtons() {
      if (!this.isDeviceTemplateManager) {
        return [];
      }
      if (this.isDraftMode) {
        return [{
          title: this.$i18n_t('Core.DetailsPage.header.buttons.save.label'),
          icon: 'fa-save',
          action: 'save-default',
          valid: this.templateInformation.templateIsChanged && this.formValidCompute,
          tooltip: !this.templateInformation.templateIsChanged ? this.$i18n_t('Core.DetailsPage.header.buttons.save.tooltip.disabled.noChanges') : '',
          dataCy: 'details-page-save-button'
        }, {
          title: this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.leaveEditModeButton'),
          icon: 'fal fa-sign-out',
          action: 'leave-template-edit-mode',
          dataCy: 'details-page-leave-edit-mode-button'
        }];
      }
      return [{
        title: this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.editTemplateButton'),
        icon: 'fal fa-pencil',
        action: 'enter-template-edit-mode',
        dataCy: 'details-page-edit-template-button'
      }];
    },
    isDeviceTemplateManager() {
      return this.permissions['DeviceTemplate.manage'];
    },
    syncFromDriverPromptButtons() {
      const buttons = [{
        text: this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.buttons.cancel'),
        emit: 'cancel'
      }];
      if (!this.templateInformation.templateIsChanged) {
        buttons.unshift({
          text: this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.buttons.sync'),
          emit: 'sync',
          disabled: this.healthIsSyncing
        });
      }
      return buttons;
    }
  },
  async created() {
    this.fetchAccounts();
  },
  mounted() {},
  methods: {
    get,
    enterTemplateEditMode() {
      this.isDraftMode = true;
    },
    leaveTemplateEditMode() {
      this.isDraftMode = false;
    },
    getHealthOperatorOptions(valueType = 'number') {
      return filterComparisonOperators(this.healthOperatorBaseOptions, {
        valueType
      });
    },
    async fetchItem(params) {
      try {
        this.linkedDevicesFetchError = null;
        const isNewTemplate = this.linkedDevicesCountForTemplateId !== this.itemId;
        if (isNewTemplate) {
          this.templateInformation.linkedDevicesTotalCount = -1;
          this.linkedDevicesCountForTemplateId = this.itemId;
        }
        let item = null;
        if (params && params.productiveVersion) {
          this.templateInformation.isProductiveVersion = true;
          item = await ApiService.deviceTemplateGetByIdProductive(this.itemId);
        } else {
          this.templateInformation.isProductiveVersion = false;
          item = await ApiService.deviceTemplateGetByIdDraft(this.itemId);
        }
        this.templateInformation.hasUnsyncedChanges = versionsHasUnsyncedChanges(item);
        this.templateInformation.unsyncedChanges = getUnsyncedChanges(item);
        if (!item.deviceMeta.connectorData) {
          item.deviceMeta.connectorData = {};
        }
        try {
          await this.buildDtwinTree(item);
        } catch (error) {
          if (error.response && this.$i18n_t(`Error.${error.response.data.message}`)) {
            this.$snack(this.$i18n_t(`Error.${error.response.data.message}`), 'error');
          } else {
            this.$snack(this.$i18n_t('Common.digitalTwins.twinNotFound'), 'error');
          }
          this.$router.push({
            path: '/device-templates'
          });
        }
        const metrics = get(item, 'healthDefinition.health', []);
        this.useExpertMode = metrics.length > 0 && !metrics.some(m => m.wizard);
        return item;
      } catch (error) {
        this.handleTemplateFetchError(error);
      }
    },
    handleTemplateFetchError(error) {
      console.error('Error fetching item:', error);
      const status = get(error, 'response.status');
      const messageKey = get(error, 'response.data.message');
      // Only a genuine "gone" (404 / DEVICE_TEMPLATE_NOT_FOUND) warrants leaving the page; transient failures
      // re-throw without skipSnack so CoreDetailsPage shows its retry alert. Both branches re-throw — returning
      // undefined would crash CoreDetailsPage on `item.isProductiveVersion`.
      const isNotFound = status === 404 || messageKey === 'DEVICE_TEMPLATE_NOT_FOUND';
      if (!isNotFound) {
        throw error;
      }
      this.$snack(this.$i18n_t(messageKey ? `Error.${messageKey}` : 'Error.DEVICE_TEMPLATE_NOT_FOUND'), 'error');
      this.$router.push({
        path: '/device-templates'
      });
      // suppress CoreDetailsPage's own error snack
      error.skipSnack = true;
      throw error;
    },
    updateUnsavedChanges(template) {
      this.templateInformation.unsyncedChanges = getUnsyncedChanges(template);
    },
    headline(item) {
      if (!item) {
        return;
      }
      return item.title || this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.deviceTemplate');
    },
    subtitle(item) {
      if (!item) {
        return;
      }
      return item.account || this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.editInformation');
    },
    async saveItem(item) {
      const healthBaseKey = 'healthDefinition.health';
      const metrics = get(item, `${healthBaseKey}`, []);
      set(item, healthBaseKey, metrics.map(m => ({
        ...m,
        wizard: !this.useExpertMode
      })));
      try {
        await ApiService.deviceTemplateUpdate(item);
      } catch (e) {
        if (e.message === 'HEALTH_OPEARTOR_NOT_ALLOWED') {
          this.$snack(this.$i18n_t('Error.HEALTH_OPEARTOR_NOT_ALLOWED', {
            operator: get(e, 'response.data.data.operator'),
            valueType: get(e, 'response.data.data.valueType')
          }), 'error');
          e.skipSnack = true;
        } else {
          this.$snack(this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.snacks.updateError'), 'error');
        }
        throw e;
      }
      const template = await ApiService.deviceTemplateGetByIdDraft(item.id);
      this.templateInformation.hasUnsyncedChanges = versionsHasUnsyncedChanges(template);
      this.templateInformation.unsyncedChanges = getUnsyncedChanges(template);
      return template;
    },
    async fetchAccounts() {
      try {
        const accounts = await this.$store.dispatch('app/loadAccounts');
        this.accounts = accounts.filter(t => !t.isRootTenant);
      } catch (error) {
        console.error(error);
        this.$snack(this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.failedToFetchAccounts'), 'error');
      }
    },
    ancestorList() {
      const extract = tree => {
        if (tree.children && tree.children.length > 0) {
          return [tree].concat(extract(tree.children[0]));
        }
        return [tree];
      };
      if (this.parentTree) {
        return cloneDeep(extract(this.parentTree).filter(i => i && i.id).concat([this.template]));
      }
      return [];
    },
    async buildDtwinTree(item) {
      let parentPartialTreePromise = ApiService.digitalTwinFetchPartialTree(this, item.parentId, undefined, undefined, ['no-children']);
      let parentObjectPromise = item.parentId ? ApiService.digitalTwinGet(this, item.parentId, true) : Promise.resolve({});
      return parentPartialTreePromise.then(result => {
        const tree = result;
        this.parentTree = get(tree, 'digitaltwins[0]', {});
        const ancestorList = this.ancestorList();
        const accountTitle = get(ancestorList, '[0].tenantName', undefined);
        const accountId = get(ancestorList, '[0].tenantId', undefined);
        if (accountTitle && accountId) {
          this.account = `${accountTitle} (ID: ${accountId})`;
        }
        return parentObjectPromise;
      });
    },
    handleDeviceMetaFormValidated(isValid) {
      this.isDeviceMetaFormValid = isValid;
    },
    handleTemplateMetaFormValidated(isValid) {
      this.isTemplateMetaFormValid = isValid;
    },
    handleDataStatesFormValidated(isValid) {
      this.isDataStatesFormValid = isValid;
    },
    handleLinkedDevicesLoaded(payload) {
      this.linkedDevicesFetchError = null;
      const linkedDevicesTotalCount = Number(get(payload, 'totalCount', 0));
      this.templateInformation.linkedDevicesTotalCount = Number.isFinite(linkedDevicesTotalCount) ? Math.max(linkedDevicesTotalCount, 0) : 0;
    },
    handleLinkedDevicesLoadError(error) {
      this.templateInformation.linkedDevicesTotalCount = -1;
      this.linkedDevicesFetchError = error;
    },
    retryLinkedDevicesFetch() {
      var _this$$refs$linkedDev;
      this.linkedDevicesFetchError = null;
      (_this$$refs$linkedDev = this.$refs.linkedDevicesList) === null || _this$$refs$linkedDev === void 0 || _this$$refs$linkedDev.retryLoadItems(true);
    },
    updateUnsyncedChanges(item) {
      this.templateInformation.hasUnsyncedChanges = versionsHasUnsyncedChanges(item);
      this.templateInformation.unsyncedChanges = getUnsyncedChanges(item);
    },
    async syncFromDeviceDriver() {
      try {
        this.healthIsSyncing = true;
        const {
          id,
          deviceDriverVersion
        } = this.$refs.detailsPage.item;
        await ApiService.deviceTemplateSyncFromDriver(id, deviceDriverVersion);
        await this.$refs.detailsPage.resetItem();
        this.$snack(this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.snacks.syncSuccess'), 'success');
      } catch (error) {
        console.error(error);
        this.$snack(this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.snacks.syncError'), 'error');
      } finally {
        this.healthIsSyncing = false;
      }
    },
    healthConfigurationTab(item, originalItem) {
      const metrics = get(item, 'healthDefinition.health', []);
      const healthBaseKey = 'healthDefinition';
      return {
        label: this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.deviceHealthConfig'),
        urlHashbang: 'tab-health-configuration',
        rows: [{
          columns: [{
            field: {
              label: this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.expertMode'),
              type: 'switch',
              readonly: !this.isDeviceTemplateManager,
              value: this.useExpertMode,
              onInput: async ({
                updateItemByFieldKey,
                waitForUserConsent,
                rerenderInput
              }) => {
                const hasConsent = await waitForUserConsent(this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.swapExpertModeDialog.title'), this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.swapExpertModeDialog.message'));
                if (!hasConsent) {
                  return;
                }
                updateItemByFieldKey(`${healthBaseKey}.health`, metrics.map(m => ({
                  ...m,
                  wizard: this.useExpertMode
                })));
                this.useExpertMode = !this.useExpertMode;
                rerenderInput();
              }
            }
          }, {
            class: 'flex-grow-0',
            field: {
              label: this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.syncFromDeviceDriver'),
              type: 'button',
              readonly: !this.isDeviceTemplateManager,
              onClick: () => {
                this.showHealthSyncPrompt = true;
              }
            }
          }]
        }, ...(this.useExpertMode ? this.healthStateExpertTabRows(item, originalItem) : this.healthStateNormalTabRows(item, originalItem))]
      };
    },
    healthStateNormalTabRows(item) {
      const healthBaseKey = 'healthDefinition.health';
      const currentHealthStates = get(item, healthBaseKey, []);
      const availableStates = get(item, 'stateDefinition', []).filter(state => state.key !== '_health').map(state => {
        const valueType = get(state, 'properties.valueType');
        return {
          label: state.key,
          value: state.key,
          valueType
        };
      });
      const availableStatesByKey = availableStates.reduce((acc, state) => {
        acc[state.value] = state;
        return acc;
      }, {});
      const valueField = state => {
        if (state.operator === 'VALUE_OLDER_THAN') {
          return {
            key: 'value',
            label: this.$i18n_t('DigitalTwin.VirtualDevice.state.health.olderThanDuration'),
            type: 'select',
            options: this.availableDurations,
            required: true
          };
        }
        return {
          key: 'value',
          label: this.$i18n_t('DigitalTwin.DigitalTwin.states.value'),
          required: true,
          dataCy: 'value_'
        };
      };
      const wiredStateField = state => {
        if (state.operator === 'VALUE_OLDER_THAN') {
          return {
            key: 'wiredState',
            label: this.$i18n_t('DigitalTwin.DigitalTwin.states.dataState'),
            type: 'text',
            readonly: true,
            value: '_raw'
          };
        }
        const currentStateIndex = currentHealthStates.indexOf(state);
        return {
          key: 'wiredState',
          label: this.$i18n_t('DigitalTwin.DigitalTwin.states.dataState'),
          type: 'select',
          options: availableStates,
          onInput: ({
            value,
            updateItemByFieldKey
          }) => {
            const baseKey = `${healthBaseKey}[${currentStateIndex}]`;
            updateItemByFieldKey(`${baseKey}.wiredState`, value);
            const selectedState = availableStatesByKey[value] || {};
            const validOperators = this.getHealthOperatorOptions(get(selectedState, 'valueType')).map(option => option.value);
            if (state.operator && !validOperators.includes(state.operator)) {
              updateItemByFieldKey(`${baseKey}.operator`, undefined);
              updateItemByFieldKey(`${baseKey}.value`, undefined);
            }
          }
        };
      };
      const operatorField = state => {
        const selectedState = availableStatesByKey[state.wiredState] || {};
        return {
          type: 'select',
          key: 'operator',
          label: this.$i18n_t('Consumers.Filter.createDialog.operator'),
          options: this.getHealthOperatorOptions(get(selectedState, 'valueType'))
        };
      };
      const inputRows = [[{
        field: {
          key: 'type',
          label: this.$i18n_t('Common.type'),
          type: 'select',
          options: [{
            value: 'warning',
            label: this.$i18n_t('DigitalTwin.VirtualDevice.state.health.typeWarning')
          }, {
            value: 'error',
            label: this.$i18n_t('DigitalTwin.VirtualDevice.state.health.typeError')
          }]
        }
      }, {
        field: {
          key: 'category',
          label: this.$i18n_t('DigitalTwin.VirtualDevice.state.health.category.label'),
          type: 'select',
          dataCy: 'select_category_',
          options: [{
            value: 'battery',
            label: this.$i18n_t('DigitalTwin.VirtualDevice.state.health.category.value.battery')
          }, {
            value: 'connectivity',
            label: this.$i18n_t('DigitalTwin.VirtualDevice.state.health.category.value.connectivity')
          }, {
            value: 'device',
            label: this.$i18n_t('DigitalTwin.VirtualDevice.state.health.category.value.device')
          }]
        }
      }, {
        field: {
          key: 'description',
          label: this.$i18n_t('DigitalTwin.VirtualDevice.state.health.wizardDescriptionField'),
          dataCy: 'description_'
        }
      }], [{
        field: wiredStateField
      }, {
        field: operatorField
      }, {
        field: valueField
      }]];
      const healthStateRows = currentHealthStates.map((state, stateIndex) => {
        return {
          class: 'v-card v-sheet elevation-2 pa-4',
          columns: [{
            rows: inputRows.map(inputRow => {
              const inputColumns = inputRow.map(input => {
                const computedField = typeof input.field === 'function' ? input.field(state) : input.field;
                return {
                  cols: input.cols,
                  sm: input.sm,
                  md: input.md,
                  lg: input.lg,
                  field: {
                    ...computedField,
                    dataCy: `${computedField.dataCy}${stateIndex}`,
                    key: `${healthBaseKey}[${stateIndex}].${computedField.key}`
                  }
                };
              });
              return {
                columns: inputColumns
              };
            })
          }, {
            class: 'flex-grow-0',
            rows: [{
              columns: [{
                field: {
                  type: 'button',
                  readonly: !this.isDeviceTemplateManager,
                  icon: 'fal fa-trash',
                  onClick: function (updateField) {
                    const newHealthStates = currentHealthStates.filter((_, index) => index !== stateIndex);
                    updateField(healthBaseKey, newHealthStates);
                  }
                }
              }]
            }]
          }]
        };
      });
      const addButtonRow = {
        columns: [{
          class: 'flex-grow-0 pl-2',
          field: {
            type: 'button',
            readonly: !this.isDeviceTemplateManager,
            icon: 'fal fa-plus',
            onClick: function (updateField) {
              let nextUnusedMetricID = 0;
              while (currentHealthStates.some(m => m.identifier === `metric_${nextUnusedMetricID}`)) {
                nextUnusedMetricID++;
              }
              const emptyState = {
                type: 'warning',
                // TODO: how to handle this?
                jsTransformer: '',
                wizard: true,
                operator: 'IS_NULL',
                identifier: `metric_${nextUnusedMetricID}`,
                wiredState: '_raw',
                category: '',
                description: '',
                value: ''
              };
              updateField(`${healthBaseKey}`, [...currentHealthStates, emptyState]);
            }
          }
        }]
      };
      const healthStateRowsWithSpacers = [];
      healthStateRows.forEach((row, index) => {
        healthStateRowsWithSpacers.push({
          subtitle: currentHealthStates[index].identifier
        });
        healthStateRowsWithSpacers.push(row);
        if (index >= healthStateRows.length - 1) {
          return;
        }
        healthStateRowsWithSpacers.push({
          isDivider: true
        });
      });
      if (healthStateRowsWithSpacers.length === 0) {
        const emptyState = {
          columns: [{
            class: 'text-center',
            field: {
              type: 'emptystate',
              icon: 'fal fa-sensor-alert',
              title: this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.healthState.emptyState.title'),
              description: this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.healthState.emptyState.description'),
              readonly: !this.isDeviceTemplateManager,
              button: this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.healthState.emptyState.buttonCreate'),
              onClick: function (updateField) {
                let nextUnusedMetricID = 0;
                while (currentHealthStates.some(m => m.identifier === `metric_${nextUnusedMetricID}`)) {
                  nextUnusedMetricID++;
                }
                const emptyState = {
                  type: 'warning',
                  // TODO: how to handle this?
                  jsTransformer: '',
                  wizard: true,
                  operator: 'IS_NULL',
                  identifier: `metric_${nextUnusedMetricID}`,
                  wiredState: '_raw',
                  category: '',
                  description: '',
                  value: ''
                };
                updateField(`${healthBaseKey}`, [...currentHealthStates, emptyState]);
              }
            }
          }]
        };
        return [emptyState];
      }
      return [...healthStateRowsWithSpacers, addButtonRow];
    },
    healthStateExpertTabRows(item) {
      const healthBaseKey = 'healthDefinition';
      const selectedStatesPreview = Object.fromEntries(get(item, `${healthBaseKey}.stateKeys`, []).map(stateKey => {
        return [stateKey, {
          value: 'LAST_VALUE',
          time: 'ISO_TIMESTAMP_LAST_UPDATE'
        }];
      }));
      const currentMetrics = get(item, `${healthBaseKey}.health`, []);
      const dataStates = item.stateDefinition;
      const metricsRows = currentMetrics.map((metric, metricIndex) => {
        return {
          subtitle: metric.identifier,
          columns: [
          // type
          {
            cols: 12,
            sm: 12,
            md: 3,
            lg: 3,
            field: {
              label: this.$i18n_t('Common.type'),
              key: `${healthBaseKey}.health[${metricIndex}].type`,
              readonly: !this.isDeviceTemplateManager,
              type: 'select',
              options: [
              // TODO: add all options
              {
                value: 'warning',
                label: this.$i18n_t('DigitalTwin.VirtualDevice.state.health.typeWarning')
              }, {
                value: 'error',
                label: this.$i18n_t('DigitalTwin.VirtualDevice.state.health.typeError')
              }]
            }
          },
          // category
          {
            cols: 12,
            sm: 12,
            md: 3,
            lg: 3,
            field: {
              label: this.$i18n_t('DigitalTwin.VirtualDevice.state.health.category.label'),
              key: `${healthBaseKey}.health[${metricIndex}].category`,
              type: 'select',
              readonly: !this.isDeviceTemplateManager,
              options: [
              // TODO: add all options
              {
                label: this.$i18n_t('DigitalTwin.VirtualDevice.state.health.category.value.battery'),
                value: 'battery'
              }, {
                label: this.$i18n_t('DigitalTwin.VirtualDevice.state.health.category.value.connectivity'),
                value: 'connectivity'
              }, {
                label: this.$i18n_t('DigitalTwin.VirtualDevice.state.health.category.value.device'),
                value: 'device'
              }]
            }
          },
          // identifier
          {
            cols: 12,
            sm: 12,
            md: 5,
            lg: 5,
            isSpacer: true
          },
          // trash button
          {
            cols: 12,
            sm: 12,
            md: 1,
            lg: 1,
            field: {
              type: 'button',
              readonly: !this.isDeviceTemplateManager,
              icon: 'fal fa-trash',
              onClick: function (updateField) {
                updateField(`${healthBaseKey}.health`, currentMetrics.filter((_, index) => index !== metricIndex));
              }
            }
          }, {
            cols: 12,
            sm: 12,
            md: 12,
            lg: 12,
            slotName: 'healthStateExpertModeMetricCodeEditor',
            field: {
              readonly: !this.isDeviceTemplateManager,
              key: `${healthBaseKey}.health[${metricIndex}].jsTransformer`
            }
          }]
        };
      });
      return [
      // select states
      {
        subtitle: this.$i18n_t('DigitalTwin.VirtualDevice.state.health.metricDescription'),
        columns: [{
          cols: 12,
          sm: 12,
          md: 12,
          lg: 12,
          field: {
            label: this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.healthState.stateKeys'),
            key: `${healthBaseKey}.stateKeys`,
            type: 'autocomplete',
            readonly: !this.isDeviceTemplateManager,
            multiple: true,
            options: (dataStates || []).filter(state => state.key !== '_health').map(s => ({
              label: s.key,
              value: s.key
            }))
          }
        }]
      },
      // quick tutorial / example
      {
        isExpansionPanel: true,
        headline: this.$i18n_t('NiotaClassic.DeviceTypes.form.parser.quickReferenceHeader'),
        subtitle: this.$i18n_t('DigitalTwin.VirtualDevice.state.health.metricExample'),
        columns: [{
          cols: 12,
          sm: 12,
          md: 12,
          lg: 12,
          field: {
            readonly: !this.isDeviceTemplateManager,
            type: 'code',
            fullWidth: true,
            content: `function (params) {
  // get battery voltage from params
  const batteryVoltage = params['battery_voltage'].value;

  // battery voltage is below 2.5V
  if (batteryVoltage < 2.5) {
    return "Battery almost empty";
  }

  // get current time/date
  const now = new Date();

  // 24hrs * 3600s/day * 1000ms/s
  const 24hoursInMilliseconds = 24 * 3600 * 1000;

  // get the last heartbeat time from params
  const lastHeartbeatTime = new Date(params['heartbeat'].time);

  // has not sent a heartbeat within 24 hours
  if ((now - lastHeartbeatTime) > 24hoursInMilliseconds) {
    return "Device seems offline";
  }

  return null;
}`
          }
        }]
      },
      // params preview
      {
        columns: [{
          cols: 12,
          sm: 12,
          md: 12,
          lg: 12,
          field: {
            readonly: !this.isDeviceTemplateManager,
            type: 'code',
            fullWidth: true,
            content: `params = ${JSON.stringify(selectedStatesPreview, null, 4)};`
          }
        }]
      }, ...metricsRows.map((row, rowIndex) => metricsRows.length > 1 && rowIndex !== metricsRows.length - 1 ? [row, {
        isDivider: true
      }] : [row]).flat(),
      // add another metric button
      {
        columns: [{
          cols: 12,
          sm: 12,
          md: 12,
          lg: 8,
          field: {
            type: 'button',
            readonly: !this.isDeviceTemplateManager,
            icon: 'fal fa-plus',
            onClick: function (updateField) {
              let nextUnusedMetricID = 0;
              while (currentMetrics.some(m => m.identifier === `metric_${nextUnusedMetricID}`)) {
                nextUnusedMetricID++;
              }
              updateField(`${healthBaseKey}.health`, [...currentMetrics, {
                identifier: `metric_${nextUnusedMetricID}`,
                wizard: false,
                wiredState: '_raw'
              }]);
            }
          }
        }]
      }];
    }
  }
};